/** Экран «Как заработать кристаллы».
 *
 * Цель двух кнопок «Как заработать» — из шторки подсказок и из диалога
 * нехватки. Игрок попадает сюда в момент, когда ему не хватило, и вопрос
 * у него ровно один: где взять. Поэтому экран не рекламирует покупки, а
 * перечисляет источники — и заканчивается кнопкой на ближайший из них.
 *
 * Все цифры приходят с сервера: они лежат в настройках и будут крутиться
 * после плейтестов, а разъехавшиеся цены в интерфейсе и при списании —
 * это жалоба, а не мелочь.
 */

import { useEffect, useState } from 'react'

import { fetchAchievements, fetchEconomy } from '../api/endpoints'
import type { EconomyInfo } from '../api/types'
import { pluralOf, t } from '../i18n'
import { backButton } from '../telegram/webapp'

export interface CrystalsScreenProps {
  streak: number
  onExit: () => void
  onOpenDaily: () => void
}

export function CrystalsScreen({ streak, onExit, onOpenDaily }: CrystalsScreenProps) {
  const [info, setInfo] = useState<EconomyInfo | null>(null)
  const [awards, setAwards] = useState({ left: 0, total: 0 })

  useEffect(() => backButton.show(onExit), [onExit])

  useEffect(() => {
    void fetchEconomy().then(setInfo).catch(() => setInfo(null))
    void fetchAchievements()
      .then((items) =>
        setAwards({
          left: items.filter((item) => !item.unlocked).length,
          total: items.length,
        }),
      )
      .catch(() => setAwards({ left: 0, total: 0 }))
  }, [])

  if (!info) {
    return (
      <div className="screen screen--center">
        <span className="hint-text">{t('app.loading')}</span>
      </div>
    )
  }

  const { earn, spend } = info

  return (
    <div className="screen">
      <header className="topbar">
        <button type="button" className="topbar__action" onClick={onExit} aria-label={t('app.back')}>
          ‹
        </button>
        <span className="topbar__title">{t('crystals.title')}</span>
        <span className="topbar__spacer" />
      </header>

      <div className="screen__body">
        <div className="card" style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 40, fontWeight: 700, color: 'var(--crystal)' }} className="num">
            ◆ {info.balance}
          </span>
          <span className="hint-text">{t('crystals.balance')}</span>
        </div>

        <span className="section-label">{t('crystals.howTo')}</span>

        <div className="stack">
          <Source
            title={t('crystals.daily')}
            hint={t('crystals.dailyHint')}
            value={t('crystals.dailyValue', { max: earn.daily_max })}
          />
          <Source
            title={t('crystals.streak')}
            hint={t('crystals.streakHint', {
              days: streak,
              word: pluralOf('plural.days', streak),
            })}
            value={t('crystals.streakValue', { amount: earn.streak_per_day })}
          />
          <Source
            title={t('crystals.win')}
            hint={t('crystals.winHint')}
            value={t('crystals.range', { min: earn.win_min, max: earn.win_max })}
          />
          <Source
            title={t('crystals.awards')}
            hint={t('crystals.awardsHint', { count: awards.left, total: awards.total })}
            value={t('crystals.range', {
              min: earn.achievement_min,
              max: earn.achievement_max,
            })}
          />
          <Source
            title={t('crystals.noHints')}
            hint={t('crystals.noHintsHint')}
            value={t('crystals.plus', { amount: earn.no_hint_bonus })}
          />
        </div>

        <div className="card" style={{ marginTop: 'var(--gap-1)' }}>
          <span style={{ fontWeight: 600, display: 'block' }}>{t('crystals.spend')}</span>
          <span className="hint-text">
            {t('crystals.spendHint', {
              check: spend.check,
              technique: spend.technique,
              reveal: spend.reveal,
              autonotes: spend.autonotes,
            })}
            {spend.theme_max > 0 && ` · ◆ ${spend.theme_min}–${spend.theme_max}`}
          </span>
        </div>

        <button
          type="button"
          className="button button--primary push-down"
          onClick={onOpenDaily}
        >
          {t('crystals.openDaily')}
        </button>
      </div>
    </div>
  )
}

function Source({ title, hint, value }: { title: string; hint: string; value: string }) {
  return (
    <div className="achievement">
      <span className="list__grow">
        <span className="shop-item__title" style={{ display: 'block' }}>
          {title}
        </span>
        <span className="hint-text">{hint}</span>
      </span>
      <span className="crystal" style={{ fontWeight: 600, flex: '0 0 auto' }}>
        {value}
      </span>
    </div>
  )
}

